import axios from 'axios';
import {API_BASE_URL} from './authApi';

export type NotificationType =
  | 'message'
  | 'follow'
  | 'post_comment'
  | 'reel_comment'
  | 'reel_share'
  | 'post_like'
  | 'post_repost';

export type NotificationFilter = 'all' | 'unread' | 'comments';
export type NotificationTargetType = 'conversation' | 'post' | 'reel' | 'user';
export type NotificationReactionType = 'like' | 'laugh' | 'cry' | 'love' | 'celebrate' | 'fire';

export interface NotificationRecord {
  id: string;
  type: NotificationType;
  recipientId: string;
  actor: {
    id: string;
    name: string;
    profilePicture?: string;
  };
  target: {
    type: NotificationTargetType;
    id: string;
  };
  metadata: {
    messageId?: string;
    commentId?: string;
    followId?: string;
    reactionType?: NotificationReactionType;
  };
  previewText?: string;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationPreferences {
  messages: boolean;
  reelShares: boolean;
  follows: boolean;
  comments: boolean;
  likes: boolean;
  reposts: boolean;
  updatedAt: string;
}

export interface NotificationUnreadSummary {
  unreadCount: number;
}

interface ListNotificationsResponse {
  success: boolean;
  notifications: NotificationRecord[];
  nextCursor?: string;
  summary?: {
    commentsCount: number;
    reactionsCount: number;
    messagesCount: number;
  };
}

interface UnreadCountResponse {
  success: boolean;
  summary: NotificationUnreadSummary;
}

interface MarkNotificationReadResponse {
  success: boolean;
  notification: NotificationRecord;
  summary: NotificationUnreadSummary;
}

interface MarkAllNotificationsReadResponse {
  success: boolean;
  readCount: number;
  summary: NotificationUnreadSummary;
}

interface NotificationPreferencesResponse {
  success: boolean;
  preferences: NotificationPreferences;
}

type ApiErrorResponse = {
  message?: string;
  error?: string | {message?: string};
};

const notificationClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data as ApiErrorResponse | undefined;
    const nestedError =
      typeof errorData?.error === 'string'
        ? errorData.error
        : errorData?.error?.message;

    return errorData?.message || nestedError || error.message || fallbackMessage;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallbackMessage;
};

const authorizedHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
});

export const notificationApi = {
  async listNotifications(
    token: string,
    options: {
      cursor?: string;
      filter?: NotificationFilter;
      limit?: number;
    } = {}
  ) {
    try {
      const response = await notificationClient.get<ListNotificationsResponse>('/notifications', {
        headers: authorizedHeaders(token),
        params: {
          cursor: options.cursor,
          filter: options.filter,
          limit: options.limit,
        },
      });

      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load notifications'));
    }
  },

  async getUnreadSummary(token: string): Promise<NotificationUnreadSummary> {
    try {
      const response = await notificationClient.get<UnreadCountResponse>('/notifications/unread-count', {
        headers: authorizedHeaders(token),
      });

      return response.data.summary;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load notification count'));
    }
  },

  async markAsRead(token: string, notificationId: string): Promise<MarkNotificationReadResponse> {
    try {
      const response = await notificationClient.put<MarkNotificationReadResponse>(
        `/notifications/${encodeURIComponent(notificationId)}/read`,
        undefined,
        {headers: authorizedHeaders(token)}
      );

      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to mark this notification as read'));
    }
  },

  async markAllAsRead(token: string): Promise<MarkAllNotificationsReadResponse> {
    try {
      const response = await notificationClient.put<MarkAllNotificationsReadResponse>(
        '/notifications/read-all',
        undefined,
        {headers: authorizedHeaders(token)}
      );

      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to mark notifications as read'));
    }
  },

  async getPreferences(token: string): Promise<NotificationPreferences> {
    try {
      const response = await notificationClient.get<NotificationPreferencesResponse>('/notifications/preferences', {
        headers: authorizedHeaders(token),
      });

      return response.data.preferences;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load notification preferences'));
    }
  },

  async updatePreferences(
    token: string,
    payload: Partial<Omit<NotificationPreferences, 'updatedAt'>>
  ): Promise<NotificationPreferences> {
    try {
      const response = await notificationClient.put<NotificationPreferencesResponse>(
        '/notifications/preferences',
        payload,
        {headers: authorizedHeaders(token)}
      );

      return response.data.preferences;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to save notification preferences'));
    }
  },
};
